import { useSyncExternalStore } from 'react';
import * as Y from 'yjs';
import { canvasRoots, type CanvasBinding, TOP_LEVEL_BINDING } from '../wiring/canvas-roots';
import { useCanvasBinding } from '../wiring/contexts/canvas-binding-context';

/**
 * Subscribes to a frame's display-data fields (`icon`, `collapsed`)
 * live from Yjs so consumers reflect collaborative changes without
 * re-rendering the whole engine on every Yjs event.
 *
 * Identity-stable snapshot — returns the SAME object reference when
 * the underlying values are unchanged. useSyncExternalStore requires
 * this: a fresh object on every getSnapshot() call triggers an
 * infinite re-render loop ("The result of getSnapshot should be
 * cached"). The cache below lives on the doc reference (WeakMap), so
 * cleanup happens automatically when the doc is GC'd.
 *
 * Returns the design-system fallback when:
 *  - no doc is provided
 *  - the node doesn't exist
 *  - the node's `data` is not a Y.Map (malformed)
 *
 * The fallback is a module-level singleton so identity stability
 * holds in the no-doc / no-node path too — otherwise a node that
 * briefly has no data flips the snapshot and triggers the loop.
 */

const FRAME_DATA_CACHE = new WeakMap<
  Y.Doc,
  Map<string, { icon: string; collapsed: boolean }>
>();

const FRAME_DATA_FALLBACK: { icon: string; collapsed: boolean } = {
  icon: 'folder',
  collapsed: false,
};

function readFrameDataSnapshot(
  doc: Y.Doc | undefined,
  nodeId: string,
  binding: CanvasBinding = TOP_LEVEL_BINDING,
): { icon: string; collapsed: boolean } {
  if (!doc) return FRAME_DATA_FALLBACK;

  const node = canvasRoots(doc, binding).nodes.get(nodeId);
  const data = node?.get('data');
  if (!(data instanceof Y.Map)) return FRAME_DATA_FALLBACK;

  const next = {
    icon: (data.get('icon') as string | undefined) ?? FRAME_DATA_FALLBACK.icon,
    collapsed: data.get('collapsed') === true,
  };

  let docCache = FRAME_DATA_CACHE.get(doc);
  if (!docCache) {
    docCache = new Map();
    FRAME_DATA_CACHE.set(doc, docCache);
  }
  const prev = docCache.get(nodeId);
  if (prev && prev.icon === next.icon && prev.collapsed === next.collapsed) {
    return prev;
  }
  docCache.set(nodeId, next);
  return next;
}

export function useFrameData(
  doc: Y.Doc | undefined,
  nodeId: string,
): { icon: string; collapsed: boolean } {
  const binding = useCanvasBinding();
  return useSyncExternalStore(
    (onChange) => {
      if (!doc) return () => {};
      const node = canvasRoots(doc, binding).nodes.get(nodeId);
      if (!node) return () => {};
      const data = node.get('data');
      if (data instanceof Y.Map) {
        data.observe(onChange);
        return () => data.unobserve(onChange);
      }
      node.observe(onChange);
      return () => node.unobserve(onChange);
    },
    () => readFrameDataSnapshot(doc, nodeId, binding),
  );
}
